import { CSSProperties } from "react";

import { notEmptyOrBlank, unique } from "./functional";
import { DropFirst } from "./types";

/*
 * Given an image URL, return the inline CSS properties to set that image as an
 * elements background image.
 */
export const getBackgroundImgStyle = (
    imgURL: string | undefined | null,
): CSSProperties => {
    if (!imgURL) {
        return {};
    }
    const styles: CSSProperties = {
        backgroundImage: `url(${imgURL})`,
    };
    return styles;
};

/**
 * Given an array of class names, combine them all together
 */
export const concatClassNames = (
    classNames: Array<string | undefined | null>,
): string => {
    const className = classNames
        .flatMap((className) => {
            return (className || "").split(" ");
        })
        .map((className) => {
            return className.trim();
        })
        .filter(notEmptyOrBlank)
        .filter(unique)
        .join(" ");
    return className;
};

/**
 * Basically the same as https://www.npmjs.com/package/classnames
 */
export const classNames = (
    names: Array<[string | null | undefined, boolean]>,
): string => {
    const filteredNames = names
        .filter(([className, include]) => className && include)
        .map(([className]) => className);
    return concatClassNames(filteredNames);
};

type StyleVariants<T extends string> = Record<T, string>;

/**
 * Given a style variant object and the style that should be selected, return
 * the appropriate class name.
 */
export const getStyleVariantClass = <T extends string>(
    variants: StyleVariants<T>,
    style: T | "" | null | undefined,
): string | undefined => {
    if (style) {
        return variants[style];
    }
    return undefined;
};

/**
 * Helper type for permitting library users to pass in a set of extra class
 * names to apply to the elements within a component.
 */
export type ExtraClassNames<ClassNames extends string> = {
    [ClassName in ClassNames]?: string;
};

/**
 * Helper function for currying a React component with the given set of extra
 * classnames. Used in conjunction with components that accept a
 * `classNames: ExtraClassNames<foo>` property.
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const withStyles = <InnerFunc extends (...args: any[]) => any>(
    innerFunc: InnerFunc,
) => {
    type AllParams = Parameters<InnerFunc>;
    type Result = ReturnType<InnerFunc>;

    type AllProps = AllParams[0];
    type RemainingParams = DropFirst<AllParams>;

    type ClassNameProps = AllProps extends { classNames?: object }
        ? Pick<AllProps, "classNames">
        : never;
    type OtherProps = AllProps extends { classNames?: object }
        ? Omit<AllProps, "classNames">
        : never;

    type WrappedFunc = (props: OtherProps, ...args: RemainingParams) => Result;

    type WrapFactory = (
        extraClassNames: ClassNameProps["classNames"],
    ) => WrappedFunc;

    const wrapFactory: WrapFactory = (extraClassNames) => {
        const wrapped: WrappedFunc = (props, ...args) => {
            const allProps: AllProps = {
                ...props,
                classNames: extraClassNames,
            };
            /* eslint-disable-next-line @typescript-eslint/no-unsafe-return */
            return innerFunc(allProps, ...args);
        };
        return wrapped;
    };
    return wrapFactory;
};
